import React, { useState, useRef, useCallback, useEffect } from 'react';import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Upload, FileText, Globe, FileType, BookOpen, Plus, Trash2,
  CheckCircle2, XCircle, Loader2, X, Sparkles, Eye, EyeOff,
  Maximize2, Minimize2, FileSearch, StickyNote, ChevronDown, ChevronRight, PlusCircle, Expand,
  Download, PanelLeftClose
} from 'lucide-react';
import { documentAPI, sourceAPI, insightAPI, notesAPI } from '../api';

export default function SourcesPanel({ isVisible, workspaceId, documents, webSources, textSources, onReload, onClosePanel }) {
  const [sourceDialog, setSourceDialog] = useState(false);
  const [sourceTab, setSourceTab] = useState('upload');
  const [webUrls, setWebUrls] = useState('');
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const fileInputRef = useRef(null);
  const [bgTasks, setBgTasks] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);

  const addBgTask = useCallback((id, label, type) => setBgTasks(prev => [...prev, { id, label, type, status: 'processing' }]), []);
  const updateBgTask = useCallback((id, status) => setBgTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t)), []);
  const removeBgTask = useCallback((id) => setBgTasks(prev => prev.filter(t => t.id !== id)), []);

  const allSources = [
    ...documents.map(d => ({ ...d, source_type: 'document' })),
    ...webSources.map(w => ({ ...w, source_type: 'web' })),
    ...textSources.map(t => ({ ...t, source_type: 'text' }))
  ];

  const handleUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSourceDialog(false);
    for (const file of files) {
      const taskId = `upload-${Date.now()}-${file.name}`;
      addBgTask(taskId, file.name, 'upload');
      (async () => {
        try {
          const fd = new FormData(); fd.append('file', file);
          await documentAPI.upload(workspaceId, fd);
          updateBgTask(taskId, 'done'); await onReload();
        } catch { updateBgTask(taskId, 'error'); }
        setTimeout(() => removeBgTask(taskId), 3000);
      })();
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [workspaceId, onReload]);

  const addWebsites = useCallback(async () => {
    const urls = webUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (!urls.length) return;
    setSourceDialog(false); setWebUrls('');
    const taskId = `web-${Date.now()}`;
    addBgTask(taskId, `${urls.length} website${urls.length > 1 ? 's' : ''}`, 'web');
    try {
      await sourceAPI.addWebBatch(workspaceId, urls);
      updateBgTask(taskId, 'done'); await onReload();
    } catch { updateBgTask(taskId, 'error'); }
    setTimeout(() => removeBgTask(taskId), 3000);
  }, [webUrls, workspaceId, onReload]);

  const addTextSource = useCallback(async () => {
    if (!textTitle.trim() || !textContent.trim()) return;
    const title = textTitle, content = textContent;
    setSourceDialog(false); setTextTitle(''); setTextContent('');
    const taskId = `text-${Date.now()}`;
    addBgTask(taskId, title, 'text');
    try {
      await sourceAPI.addText(workspaceId, { title, content });
      updateBgTask(taskId, 'done'); await onReload();
    } catch { updateBgTask(taskId, 'error'); }
    setTimeout(() => removeBgTask(taskId), 3000);
  }, [textTitle, textContent, workspaceId, onReload]);

  const deleteSource = async (type, id) => {
    try {
      if (type === 'document') await documentAPI.delete(id);
      else if (type === 'web') await sourceAPI.deleteWeb(id);
      else if (type === 'text') await sourceAPI.deleteText(id);
      setSelectedSource(null);
      await onReload();
    } catch (e) { console.error('Delete error:', e); }
  };

  const toggleSourceActive = async (sourceId, newActive) => {
    try {
      await sourceAPI.toggleActive(sourceId, newActive);
      await onReload();
    } catch (e) { console.error('Toggle error:', e); }
  };

  return (
    <div className={`bg-white flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${isVisible ? 'w-[374px] min-w-[374px] max-w-[374px] border-r border-[#E5E7EB]' : 'w-0 min-w-0 max-w-0 border-r-0'}`} data-testid="sources-panel">
      <div className="p-3 border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Sources</h2>
            <span className="text-sm px-2 py-0.5 bg-[#F3F4F6] text-[#4B5563] rounded-full font-medium">{allSources.length}</span>
          </div>
          {onClosePanel && (
            <button
              onClick={onClosePanel}
              className="p-1.5 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0"
              data-testid="sources-panel-close-btn"
              title="Hide sources panel"
              aria-label="Hide sources panel"
            >
              <PanelLeftClose className="w-4 h-4 text-[#4B5563]" />
            </button>
          )}
        </div>
        <Dialog open={sourceDialog} onOpenChange={setSourceDialog}>
          <DialogTrigger asChild>
            <button className="w-full h-9 border border-dashed border-[#D1D5DB] hover:border-[#6590FF] text-base font-medium text-[#4B5563] hover:text-[#6590FF] flex items-center justify-center gap-1.5 rounded-xl transition-colors" data-testid="add-sources-btn">
              <Plus className="w-4 h-4" /> Add sources
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl border border-[#E5E7EB] max-w-lg">
            <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Add Sources</DialogTitle></DialogHeader>
            <SourceTabs sourceTab={sourceTab} setSourceTab={setSourceTab} />
            {sourceTab === 'upload' && <UploadTab fileInputRef={fileInputRef} handleUpload={handleUpload} />}
            {sourceTab === 'web' && <WebTab webUrls={webUrls} setWebUrls={setWebUrls} addWebsites={addWebsites} />}
            {sourceTab === 'text' && <TextTab textTitle={textTitle} setTextTitle={setTextTitle} textContent={textContent} setTextContent={setTextContent} addTextSource={addTextSource} />}
          </DialogContent>
        </Dialog>
      </div>

      {bgTasks.length > 0 && (
        <div className="px-3 py-2 border-b border-[#E5E7EB] bg-[#F9FAFB]" data-testid="bg-processing-banner">
          {bgTasks.map(task => (
            <div key={task.id} className="flex items-center gap-2 py-1">
              {task.status === 'processing' ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#6590FF] flex-shrink-0" /> :
               task.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5 text-[#059669] flex-shrink-0" /> :
               <XCircle className="w-3.5 h-3.5 text-[#DC2626] flex-shrink-0" />}
              <span className="text-sm text-[#4B5563] truncate flex-1">
                {task.status === 'processing' ? 'Processing' : task.status === 'done' ? 'Added' : 'Failed'}: {task.label}
              </span>
            </div>
          ))}
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          {allSources.length === 0 && bgTasks.length === 0 ? (
            <div className="p-6 text-center">
              <BookOpen className="w-8 h-8 text-[#D1D5DB] mx-auto mb-2" />
              <p className="text-base text-[#4B5563]">Saved sources will appear here</p>
              <p className="text-sm text-[#9CA3AF] mt-1">Click Add sources above</p>
            </div>
          ) : allSources.map(src => (
            <SourceItem key={src.id} src={src} onClick={() => setSelectedSource(src)} />
          ))}
        </div>
        {/* Notes Section */}
        <NotesSection workspaceId={workspaceId} onReload={onReload} />
      </ScrollArea>

      {selectedSource && (
        <SourceDetailPopup
          source={selectedSource}
          workspaceId={workspaceId}
          onClose={() => setSelectedSource(null)}
          onDelete={deleteSource}
          onToggle={toggleSourceActive}
          onReload={onReload}
        />
      )}
    </div>
  );
}

function SourceItem({ src, onClick }) {
  const bgColor = src.source_type === 'document' ? '#EEF2FF' : src.source_type === 'web' ? '#FEF3C7' : '#ECFDF5';
  const name = src.title || 'Untitled';
  const displayName = name.length > 35 ? name.slice(0, 35) + '...' : name;
  const isInactive = src.is_active === false;
  const hasSummary = !!src.summary;
  // Short preview: first 60 chars of summary
  const preview = src.summary ? (src.summary.length > 80 ? src.summary.slice(0, 80) + '...' : src.summary) : null;

  return (
    <button onClick={onClick} title={name}
      className={`w-full group flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-[#F9FAFB] transition-colors text-left ${isInactive ? 'opacity-45' : ''}`}
      data-testid={`source-${src.id}`}>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: bgColor }}>
        {src.source_type === 'document' && <FileText className="w-4 h-4 text-[#6590FF]" />}
        {src.source_type === 'web' && <Globe className="w-4 h-4 text-[#D97706]" />}
        {src.source_type === 'text' && <FileType className="w-4 h-4 text-[#059669]" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-medium text-[#111827] leading-tight">{displayName}</p>
        {preview && (
          <p className="text-xs text-[#9CA3AF] leading-snug mt-0.5 line-clamp-2">{preview}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[11px] text-[#9CA3AF]">{src.chunk_count || 0} chunks</span>
          {isInactive && <span className="text-[11px] text-[#DC2626] font-medium">Excluded</span>}
          {hasSummary && <span className="text-[11px] text-[#059669] font-medium flex items-center gap-0.5"><Sparkles className="w-2.5 h-2.5" />Summary</span>}
        </div>
      </div>
      <div className="flex-shrink-0 mt-0.5">
        {src.status === 'ready' ? <CheckCircle2 className="w-3.5 h-3.5 text-[#059669]" /> :
         src.status === 'error' ? <XCircle className="w-3.5 h-3.5 text-[#DC2626]" /> :
         <Loader2 className="w-3.5 h-3.5 animate-spin text-[#6B7280]" />}
      </div>
    </button>
  );
}

export function SourceDetailPopup({ source, workspaceId, onClose, onDelete, onToggle, onReload, initialTab, highlightChunkText, highlightPage }) {
  const [fullscreen, setFullscreen] = useState(false);
  const isNoteSource = source.source_type === 'text' && (source.title || '').startsWith('Note:');
  const [tab, setTab] = useState(initialTab || (isNoteSource ? 'content' : 'summary'));
  const [content, setContent] = useState(null);
  const [summary, setSummary] = useState(source.summary || null);
  const [loading, setLoading] = useState(true);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [isActive, setIsActive] = useState(source.is_active !== false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const highlightRef = useRef(null);

  useEffect(() => {
    loadContent();
  }, [source.id]);

  // Auto-scroll to highlighted chunk once content is loaded.
  useEffect(() => {
    if (!loading && tab === 'content' && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, tab, content]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const res = await sourceAPI.getContent(source.id);
      setContent(res.data);
      if (res.data.summary) setSummary(res.data.summary);
      if (res.data.is_active !== undefined) setIsActive(res.data.is_active);
    } catch (e) { console.error('Load content error:', e); }
    setLoading(false);
  };

  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await insightAPI.sourceSummary(workspaceId, source.id);
      setSummary(res.data.summary);
    } catch (e) { setSummary('Unable to generate summary.'); }
    setGeneratingSummary(false);
  };

  const handleToggle = async () => {
    const newActive = !isActive;
    setIsActive(newActive);
    await onToggle(source.id, newActive);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(source.source_type, source.id);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await documentAPI.download(source.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = source.title || `document-${source.id}`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { console.error('Download error:', e); }
    setDownloading(false);
  };

  // Decide which chunk (if any) should be highlighted + scrolled into view.
  const highlightIdx = (() => {
    if (!content?.chunks?.length) return -1;
    if (highlightChunkText) {
      const needle = highlightChunkText.trim().slice(0, 80).toLowerCase();
      if (needle) {
        const idx = content.chunks.findIndex(c => (c.text || '').toLowerCase().includes(needle));
        if (idx >= 0) return idx;
      }
    }
    if (highlightPage && highlightPage > 0) {
      return content.chunks.findIndex(c => c.page === highlightPage);
    }
    return -1;
  })();

  const name = source.title || 'Untitled';
  const typeLabel = source.source_type === 'document' ? 'Document' : source.source_type === 'web' ? 'Website' : 'Text';
  const bgColor = source.source_type === 'document' ? '#EEF2FF' : source.source_type === 'web' ? '#FEF3C7' : '#ECFDF5';
  const iconColor = source.source_type === 'document' ? '#6590FF' : source.source_type === 'web' ? '#D97706' : '#059669';

  const panelCls = fullscreen
    ? 'fixed inset-4 z-50 bg-white rounded-2xl shadow-2xl flex flex-col'
    : 'bg-white w-full max-w-xl rounded-2xl shadow-2xl flex flex-col max-h-[85vh]';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="source-detail-overlay">
      <div className={panelCls} onClick={e => e.stopPropagation()} data-testid="source-detail-dialog">
        {/* Header */}
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bgColor }}>
              {source.source_type === 'document' && <FileText className="w-4 h-4" style={{ color: iconColor }} />}
              {source.source_type === 'web' && <Globe className="w-4 h-4" style={{ color: iconColor }} />}
              {source.source_type === 'text' && <FileType className="w-4 h-4" style={{ color: iconColor }} />}
            </div>
            <p className="text-base font-bold text-[#111827] truncate" title={name} style={{ fontFamily: 'var(--font-heading)' }}>{name}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setFullscreen(!fullscreen)} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors" title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              {fullscreen ? <Minimize2 className="w-4 h-4 text-[#4B5563]" /> : <Maximize2 className="w-4 h-4 text-[#4B5563]" />}
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors"><X className="w-4 h-4 text-[#4B5563]" /></button>
          </div>
        </div>

        {/* Meta + tabs */}
        <div className="px-5 py-2.5 border-b border-[#E5E7EB] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#4B5563] bg-[#F3F4F6] px-2 py-1 rounded-lg">{typeLabel}</span>
            <span className="text-sm text-[#9CA3AF]">{source.chunk_count || 0} chunks</span>
            <span className={`text-sm font-medium ${source.status === 'ready' ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
              {source.status === 'ready' ? 'Indexed' : source.status || 'Processing'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setTab('summary')}
              className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${tab === 'summary' ? 'bg-[#6590FF] text-white' : 'text-[#4B5563] hover:bg-[#F3F4F6]'}`}
              data-testid="tab-summary">Summary</button>
            <button onClick={() => setTab('content')}
              className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${tab === 'content' ? 'bg-[#6590FF] text-white' : 'text-[#4B5563] hover:bg-[#F3F4F6]'}`}
              data-testid="tab-content">Content</button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12">
              <Loader2 className="w-5 h-5 animate-spin text-[#6590FF]" />
              <span className="text-base text-[#4B5563]">Loading source data...</span>
            </div>
          ) : tab === 'summary' ? (
            <div>
              {summary ? (
                <div className="prose prose-sm max-w-none text-[#374151] leading-relaxed" data-testid="source-summary-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
                </div>
              ) : generatingSummary ? (
                <div className="flex items-center justify-center gap-2 py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-[#6590FF]" />
                  <span className="text-base text-[#4B5563]">Generating AI summary...</span>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Sparkles className="w-8 h-8 text-[#D1D5DB] mx-auto mb-3" />
                  <p className="text-base text-[#4B5563] mb-3">No summary available yet</p>
                  <button onClick={generateSummary}
                    className="h-9 px-4 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-base font-medium rounded-xl transition-colors flex items-center gap-2 mx-auto"
                    data-testid="generate-summary-btn">
                    <Sparkles className="w-4 h-4" /> Generate Summary
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              {content?.chunks?.length > 0 ? (
                <div className="space-y-3">
                  {content.chunks.map((chunk, i) => {
                    const isHighlight = i === highlightIdx;
                    return (
                      <div
                        key={`chunk-${chunk.page || 0}-${i}`}
                        ref={isHighlight ? highlightRef : null}
                        className={`p-3 rounded-xl border transition-colors ${isHighlight ? 'bg-[#FEF9C3] border-[#F59E0B] ring-2 ring-[#F59E0B]/40' : 'bg-[#F9FAFB] border-[#E5E7EB]'}`}
                        data-testid={isHighlight ? 'chunk-highlighted' : `chunk-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          {chunk.page > 0 ? (
                            <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">Page {chunk.page}</p>
                          ) : <span />}
                          {isHighlight && (
                            <span className="text-[11px] font-bold text-[#92400E] uppercase tracking-wider flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> Cited passage
                            </span>
                          )}
                        </div>
                        <p className="text-base text-[#374151] leading-relaxed whitespace-pre-wrap">{chunk.text}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileSearch className="w-8 h-8 text-[#D1D5DB] mx-auto mb-2" />
                  <p className="text-base text-[#4B5563]">No extracted content available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions (only shown when caller provides workspace-management handlers) */}
        {(onToggle || onDelete) ? (
          <div className="px-5 py-3 border-t border-[#E5E7EB] flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              {onToggle && (
                <button onClick={handleToggle}
                  className={`h-9 px-3 flex items-center gap-2 text-base font-medium rounded-xl transition-colors ${isActive ? 'text-[#059669] hover:bg-[#ECFDF5]' : 'text-[#9CA3AF] bg-[#F3F4F6]'}`}
                  data-testid="toggle-source-btn">
                  {isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {isActive ? 'Active in workspace' : 'Excluded from workspace'}
                </button>
              )}
              {source.source_type === 'document' && (
                <button onClick={handleDownload} disabled={downloading}
                  className="h-9 px-3 flex items-center gap-2 text-base font-medium text-[#6590FF] hover:bg-[#EEF2FF] rounded-xl transition-colors disabled:opacity-50"
                  data-testid="download-source-btn">
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download
                </button>
              )}
            </div>
            {onDelete && (
              <button onClick={handleDelete} disabled={deleting}
                className="h-9 px-3 flex items-center gap-2 text-base font-medium text-[#DC2626] hover:bg-[#FEE2E2] rounded-xl transition-colors disabled:opacity-50"
                data-testid="delete-source-btn">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove
              </button>
            )}
          </div>
        ) : source.source_type === 'document' ? (
          <div className="px-5 py-3 border-t border-[#E5E7EB] flex items-center justify-end flex-shrink-0">
            <button onClick={handleDownload} disabled={downloading}
              className="h-9 px-3 flex items-center gap-2 text-base font-medium text-[#6590FF] hover:bg-[#EEF2FF] rounded-xl transition-colors disabled:opacity-50"
              data-testid="download-source-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SourceTabs({ sourceTab, setSourceTab }) {
  const tabs = [
    { key: 'upload', icon: Upload, label: 'Upload files' },
    { key: 'web', icon: Globe, label: 'Websites' },
    { key: 'text', icon: FileType, label: 'Copied text' }
  ];
  return (
    <div className="flex gap-1 border-b border-[#E5E7EB] mb-4">
      {tabs.map(t => (
        <button key={t.key} onClick={() => setSourceTab(t.key)}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-base font-medium border-b-2 transition-colors ${sourceTab === t.key ? 'border-[#6590FF] text-[#6590FF]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'}`}
          data-testid={`source-tab-${t.key}`}>
          <t.icon className="w-4 h-4" />{t.label}
        </button>
      ))}
    </div>
  );
}

function UploadTab({ fileInputRef, handleUpload }) {
  return (
    <div>
      <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.txt,.pptx,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff,.tif" className="hidden" onChange={handleUpload} data-testid="file-input" />
      <button onClick={() => fileInputRef.current?.click()}
        className="w-full h-32 border-2 border-dashed border-[#D1D5DB] hover:border-[#6590FF] rounded-xl flex flex-col items-center justify-center gap-2 transition-colors" data-testid="upload-drop-zone">
        <Upload className="w-6 h-6 text-[#9CA3AF]" />
        <span className="text-base text-[#4B5563]">Drop files or click to upload</span>
        <span className="text-sm text-[#9CA3AF]">PDF, DOCX, PPTX, XLSX, CSV, TXT, Images (OCR)</span>
      </button>
      <p className="text-sm text-[#9CA3AF] mt-2 text-center">Files will process in the background</p>
    </div>
  );
}

function WebTab({ webUrls, setWebUrls, addWebsites }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-base font-medium text-[#4B5563] mb-1.5 block">Website URLs (one per line)</Label>
        <Textarea value={webUrls} onChange={e => setWebUrls(e.target.value)}
          placeholder={"https://example.com/page1\nhttps://example.com/page2"}
          rows={5} className="rounded-xl resize-none font-mono text-base" data-testid="web-urls-textarea" />
      </div>
      <button onClick={addWebsites} disabled={!webUrls.trim()}
        className="w-full h-10 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-base font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="add-websites-btn">
        <Globe className="w-4 h-4" /> Add Websites
      </button>
    </div>
  );
}

function TextTab({ textTitle, setTextTitle, textContent, setTextContent, addTextSource }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-base font-medium text-[#4B5563] mb-1.5 block">Title</Label>
        <Input value={textTitle} onChange={e => setTextTitle(e.target.value)} placeholder="Note title" className="rounded-xl h-10" data-testid="text-title-input" />
      </div>
      <div>
        <Label className="text-base font-medium text-[#4B5563] mb-1.5 block">Content</Label>
        <Textarea value={textContent} onChange={e => setTextContent(e.target.value)} placeholder="Paste or type your text here..." rows={6} className="rounded-xl resize-none" data-testid="text-content-input" />
      </div>
      <button onClick={addTextSource} disabled={!textTitle.trim() || !textContent.trim()}
        className="w-full h-10 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-base font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="add-text-btn">
        <FileType className="w-4 h-4" /> Add Text
      </button>
    </div>
  );
}


function NotesSection({ workspaceId, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [previewNote, setPreviewNote] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTitle, setComposerTitle] = useState('');
  const [composerContent, setComposerContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (expanded && notes.length === 0) loadNotes();
  }, [expanded]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await notesAPI.list(workspaceId);
      setNotes(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const saveComposer = async () => {
    if (!composerContent.trim()) return;
    setSaving(true);
    try {
      const res = await notesAPI.create(workspaceId, { title: composerTitle.trim(), content: composerContent.trim() });
      const newNote = res?.data || { id: Date.now().toString(), title: composerTitle, content: composerContent, source_id: null, date_created: new Date().toISOString() };
      setNotes(prev => [newNote, ...prev]);
      setComposerTitle(''); setComposerContent(''); setComposerOpen(false);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const addToSources = async (noteId) => {
    setAddingId(noteId);
    try {
      const res = await notesAPI.addToSources(workspaceId, noteId);
      const newSourceId = res?.data?.id || null;
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, source_id: newSourceId || n.source_id } : n));
      // Also keep the open popup in sync so its action buttons update.
      setPreviewNote(p => (p && p.id === noteId) ? { ...p, source_id: newSourceId || p.source_id } : p);
      onReload();
    } catch (e) { console.error(e); }
    setAddingId(null);
  };

  const deleteNote = async (noteId) => {
    try {
      await notesAPI.delete(workspaceId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      setPreviewNote(p => (p && p.id === noteId) ? null : p);
      onReload();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="w-full flex-shrink-0 border-t border-[#E5E7EB] mt-1 pt-1 px-2 overflow-hidden" data-testid="notes-section">
      <div className="flex items-center gap-1">
        <button onClick={() => { setExpanded(!expanded); if (!expanded) loadNotes(); }}
          className="flex-1 flex items-center gap-2 py-2 px-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors"
          data-testid="notes-section-toggle">
          {expanded ? <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />}
          <StickyNote className="w-4 h-4 text-[#4B5563]" />
          <span className="text-base font-bold text-[#111827]">Notes</span>
          {notes.length > 0 && <span className="ml-auto text-xs text-[#9CA3AF] bg-[#F3F4F6] px-2 py-0.5 rounded-full">{notes.length}</span>}
        </button>
        {expanded && (
          <button onClick={() => { setComposerOpen(v => !v); }}
            className="p-1.5 hover:bg-[#EEF2FF] rounded-xl transition-colors flex-shrink-0"
            title={composerOpen ? 'Cancel' : 'Add a note'}
            data-testid="notes-add-btn">
            {composerOpen ? <X className="w-4 h-4 text-[#4B5563]" /> : <Plus className="w-4 h-4 text-[#6590FF]" />}
          </button>
        )}
      </div>

      {expanded && composerOpen && (
        <div className="w-full mt-1 mb-2 p-2 border border-[#C7D2FE] rounded-xl bg-[#EEF2FF]/40 space-y-1.5 overflow-hidden" data-testid="notes-composer">
          <Input value={composerTitle} onChange={e => setComposerTitle(e.target.value)}
            placeholder="Note title (optional)"
            className="h-8 text-sm rounded-lg bg-white" data-testid="notes-composer-title" />
          <Textarea value={composerContent} onChange={e => setComposerContent(e.target.value)}
            placeholder="Write your note…"
            className="min-h-[72px] text-sm rounded-lg bg-white resize-none" data-testid="notes-composer-content" />
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => { setComposerOpen(false); setComposerTitle(''); setComposerContent(''); }}
              className="h-7 px-2 text-xs font-medium text-[#4B5563] hover:bg-[#F3F4F6] rounded-xl"
              data-testid="notes-composer-cancel">Cancel</button>
            <button onClick={saveComposer} disabled={!composerContent.trim() || saving}
              className="h-7 px-3 text-xs font-semibold text-white bg-[#6590FF] hover:bg-[#4B7CFF] disabled:opacity-50 rounded-xl flex items-center gap-1"
              data-testid="notes-composer-save">
              {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save Note
            </button>
          </div>
        </div>
      )}

      {expanded && (
        <div className="w-full mt-1 space-y-1 pb-2 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#6590FF]" /></div>
          ) : notes.length === 0 && !composerOpen ? (
            <p className="text-sm text-[#9CA3AF] px-2 py-2">No notes yet. Click <Plus className="w-3 h-3 inline -mt-0.5" /> to write one or save AI responses from chat.</p>
          ) : (
            notes.map(note => {
              const inSources = !!note.source_id;
              return (
                <div key={note.id}
                  onClick={() => setPreviewNote(note)}
                  className="w-full p-2 border border-[#E5E7EB] rounded-xl hover:bg-[#F9FAFB] hover:border-[#C7D2FE] group cursor-pointer transition-colors overflow-hidden"
                  data-testid={`note-${note.id}`}>
                  <div className="flex items-start gap-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="text-sm font-semibold text-[#111827] truncate flex-1 min-w-0">{note.title || 'Untitled Note'}</p>
                        {inSources && <CheckCircle2 className="w-3 h-3 text-[#059669] flex-shrink-0" title="In Sources" />}
                      </div>
                      <p className="text-xs text-[#6B7280] line-clamp-2 mt-0.5 break-words">{note.content?.slice(0, 100)}</p>
                    </div>
                    <Expand className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#6590FF] flex-shrink-0 mt-0.5" data-testid={`note-expand-${note.id}`} />
                  </div>
                  <div className="flex items-center gap-1 mt-2 pt-1.5 border-t border-[#F3F4F6]">
                    {!inSources ? (
                      <button onClick={(e) => { e.stopPropagation(); addToSources(note.id); }} disabled={addingId === note.id}
                        className="h-6 px-2.5 flex items-center gap-1 text-[11px] font-semibold text-white bg-[#6590FF] hover:bg-[#4B7CFF] disabled:opacity-50 rounded-xl transition-colors"
                        data-testid={`note-add-source-${note.id}`}>
                        {addingId === note.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlusCircle className="w-3 h-3" />}
                        Add to Sources
                      </button>
                    ) : (
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-[#059669]" data-testid={`note-in-sources-${note.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> In Sources
                      </span>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}
                      className="ml-auto p-1 hover:bg-[#FEE2E2] rounded transition-colors"
                      data-testid={`note-delete-${note.id}`}
                      title="Delete note">
                      <Trash2 className="w-3 h-3 text-[#DC2626]" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {previewNote && (
        <NotePreviewPopup
          note={previewNote}
          onClose={() => setPreviewNote(null)}
          onAddToSources={() => addToSources(previewNote.id)}
          onDelete={() => deleteNote(previewNote.id)}
          adding={addingId === previewNote.id}
        />
      )}
    </div>
  );
}

function NotePreviewPopup({ note, onClose, onAddToSources, onDelete, adding }) {
  const [fullscreen, setFullscreen] = useState(false);
  const inSources = !!note.source_id;
  const panelCls = fullscreen
    ? 'fixed inset-4 z-50 bg-white rounded-2xl shadow-2xl flex flex-col'
    : 'bg-white w-full max-w-xl rounded-2xl shadow-2xl flex flex-col max-h-[85vh]';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="note-detail-overlay">
      <div className={panelCls} onClick={e => e.stopPropagation()} data-testid="note-detail-dialog">
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#FEF9C3]">
              <StickyNote className="w-4 h-4 text-[#A16207]" />
            </div>
            <p className="text-base font-bold text-[#111827] truncate" title={note.title || 'Untitled Note'} style={{ fontFamily: 'var(--font-heading)' }}>
              {note.title || 'Untitled Note'}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setFullscreen(!fullscreen)} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors" title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              {fullscreen ? <Minimize2 className="w-4 h-4 text-[#4B5563]" /> : <Maximize2 className="w-4 h-4 text-[#4B5563]" />}
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors" data-testid="note-detail-close">
              <X className="w-4 h-4 text-[#4B5563]" />
            </button>
          </div>
        </div>

        <div className="px-5 py-2.5 border-b border-[#E5E7EB] flex items-center gap-3 flex-shrink-0">
          <span className="text-sm font-medium text-[#4B5563] bg-[#F3F4F6] px-2 py-1 rounded-lg">Note</span>
          {inSources ? (
            <span className="text-sm font-medium text-[#059669] flex items-center gap-1" data-testid="note-detail-in-sources">
              <CheckCircle2 className="w-3 h-3" /> Indexed in Sources
            </span>
          ) : (
            <span className="text-sm text-[#9CA3AF]">Not in Sources</span>
          )}
          {note.date_created && <span className="text-sm text-[#9CA3AF] ml-auto">{new Date(note.date_created).toLocaleString()}</span>}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="prose prose-sm max-w-none text-[#374151] leading-relaxed" data-testid="note-detail-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.content || ''}</ReactMarkdown>
          </div>
        </div>

        <div className="border-t border-[#E5E7EB] px-5 py-3 flex items-center justify-between gap-2 flex-shrink-0">
          <button onClick={onDelete}
            className="h-8 px-3 text-sm font-medium text-[#DC2626] hover:bg-[#FEE2E2] rounded-xl flex items-center gap-1.5"
            data-testid="note-detail-delete">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
          {!inSources && (
            <button onClick={onAddToSources} disabled={adding}
              className="h-8 px-3 text-sm font-semibold text-white bg-[#6590FF] hover:bg-[#4B7CFF] disabled:opacity-50 rounded-xl flex items-center gap-1.5"
              data-testid="note-detail-add-source">
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlusCircle className="w-3.5 h-3.5" />}
              Add to Sources
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
